import { ClassImporterExporter, ImportMap } from "@app/shared/resources/class-importer-exporter.service";

import { DbHelper } from "@app/shared/model/db-object.model";
import { UserRole } from "./user-role";

/**
 * @description A model to describe a user in code
 * @interface User
 */
export interface User {
    readonly entityId: number;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    readonly id?: number;
    readonly gitkitId?: string;
    readonly roleId?: number | string;
    permissions?: string[];
    role?: UserRole;
    readonly accountNumber?: string | number;
    inviteCode?: string;
    phoneType?: string;
    phone2?: number | string;
    phone2Type?: string;
    phone3?: number | string;
    phone3Type?: string;
}

export interface UserInfo {
    userinfo: User;
}

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * @description A model to describe a user in the database
 * @interface UserData
 */
export interface UserData {
    entity_id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    id: number;
    git_id: string;
    role_id: number;
    account_number: string;
    invite_code: string;
    phone_type: string;
    phone_2: string;
    phone_2_type: string;
    phone_3: string;
    phone_3_type: string;
    role?: UserRole;
    permissions?: string[];
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * https://pkg.go.dev/firebase.google.com/go@v3.13.0+incompatible/auth#UserInfo
 */
export interface FirebaseUserRecord {
    displayName?: string;
    email?: string;
    phoneNumber?: string;
    photoURL?: string;
    providerId?: string;
    rawId?: string;
}

/**
 * @description A model to describe an authenticated user returned by the websocket on a connection attempt
 *              https://pkg.go.dev/firebase.google.com/go@v3.13.0+incompatible/auth#UserRecord
 */
export interface AuthenticatedWSUser extends FirebaseUserRecord {
    CustomClaims: Record<any, any> | null;
    Disabled: boolean;
    EmailVerified: boolean;
    ProviderUserInfo: FirebaseUserRecord[];
    TenantID: string;
    TokensValidAfterMillis: number;
    UserMetadata: { CreationTimestamp: number; LastLogInTimestamp: number; LastRefreshTimestamp: number };
}

export class UserHelper extends DbHelper<User, UserData> {
    dbTypeName = "Staff Member";
    dbTypeNamePlural = "Staff Members";

    readonly importMap: ImportMap<User, UserData> = [
        { i: "entity_id", o: "entityId" },
        { i: "email", o: "email" },
        { i: "first_name", o: "firstName" },
        { i: "last_name", o: "lastName" },
        { i: "phone", o: "phone" },
        { i: "id", o: "id" },
        { i: "git_id", o: "gitkitId" },
        { i: "role_id", o: "roleId" },
        { i: "account_number", o: "accountNumber" },
        { i: "invite_code", o: "inviteCode" },
        { i: "phone_type", o: "phoneType" },
        { i: "phone_2", o: "phone2" },
        { i: "phone_2_type", o: "phone2Type" },
        { i: "phone_3", o: "phone3" },
        { i: "phone_3_type", o: "phone3Type" },
    ];

    /**
     * @description Converts a database user object to a rekeyed user object for use in code.
     * @param {UserData} userData a user object from the database
     * @returns {User} a fully typed user object
     */
    fromDbObject(userData: Partial<UserData>): User {
        const user = ClassImporterExporter.importObject(userData, this.importMap);
        user.role = userData.role;
        // don't believe this is needed, but should confirm
        user.permissions = undefined;
        return user;
    }

    toDbObject(obj: User): UserData {
        const data = super.toDbObject(obj);

        data.role_id = +data.role_id || null;
        data.entity_id = +data.entity_id || null;
        return data;
    }

    static getFullName = (user: User): string => `${user.firstName} ${user.lastName}`;

    // using string concat to avoid white space charaters
    /**
     *
     * @param user
     * @returns {string} log data of affecting user as string
     */
    static getLogString = (user: User): string =>
        `Name: ${UserHelper.getFullName(user)}<br>` + `Email: ${user.email}<br>` + `Profile: ${JSON.stringify(user)}`;

    static getModifiedLogString = (prev: User, curr: User): string =>
        `Original Name: ${UserHelper.getFullName(prev)}<br>` +
        `Email: ${prev.email}<br>` +
        `Profile: ${JSON.stringify(prev)}<br>` +
        `New Name: ${UserHelper.getFullName(curr)}<br>` +
        `Email: ${curr.email}<br>` +
        `Profile: ${JSON.stringify(curr)}`;
}

const USER_HELPER_SINGLETON: UserHelper = new UserHelper();
export { USER_HELPER_SINGLETON as userHelper };
